/* eslint-disable react-hooks/set-state-in-effect */
import { useCallback, useEffect, useState } from 'react'
import { kanbify } from '../lib/kanbifyClient'
import { logDevError } from '../lib/logger'

/* ========== CARD ATTACHMENTS ========== */
export function useAttachments(cardId) {
    const [attachments, setAttachments] = useState([])

    // Order is user-controlled: explicit `position` first, then upload time
    // as a stable tiebreaker (older rows backfilled to 0 stay in created order).
    const sortAtt = (arr) => [...arr].sort((a, b) =>
        (a.position ?? 0) - (b.position ?? 0) ||
        new Date(a.created_at) - new Date(b.created_at)
    )

    const fetch = useCallback(async () => {
        if (!cardId) return
        const { data } = await kanbify
            .from('attachments')
            .select('*, profiles:created_by ( id, full_name, avatar_url )')
            .eq('card_id', cardId)
            .order('position', { ascending: true })
            .order('created_at', { ascending: true })
        setAttachments(sortAtt(data || []))
    }, [cardId])

    useEffect(() => { fetch() }, [fetch])

    const uploadAttachment = async (file, userId, options = {}) => {
        // Normalize with uploadAttachments: video allowed up to 100MB, other 10MB.
        const isVid = (file.type || '').startsWith('video/') || /\.(mp4|webm|mov|m4v|ogg)$/i.test(file.name || '')
        const MAX_FILE_SIZE = isVid ? 100 * 1024 * 1024 : 10 * 1024 * 1024
        if (file.size > MAX_FILE_SIZE) {
            const mb = (MAX_FILE_SIZE / 1024 / 1024).toFixed(0)
            return { error: `Fișierul este prea mare. Dimensiunea maximă este ${mb}MB (fișierul tău: ${(file.size / 1024 / 1024).toFixed(1)}MB).` }
        }

        const fileExt = file.name.split('.').pop()
        const fileName = `${cardId}/${Math.random().toString(36).substring(2)}.${fileExt}`

        const { error: uploadError } = await kanbify.storage
            .from('card_attachments')
            .upload(fileName, file)

        if (uploadError) return { error: uploadError.message }

        const { data: { publicUrl } } = kanbify.storage
            .from('card_attachments')
            .getPublicUrl(fileName)

        // Append at the end: new screenshot lands after everything already there.
        let nextPos = 0
        setAttachments(prev => {
            nextPos = prev.reduce((m, a) => Math.max(m, (a.position ?? 0) + 1), 0)
            return prev
        })

        const row = {
                card_id: cardId,
                name: file.name,
                url: publicUrl,
                type: file.type,
                created_by: userId,
                position: nextPos,
            }
        if (options.commentId) row.comment_id = options.commentId

        const { data, error: dbError } = await kanbify.from('attachments')
            .insert([row])
            .select()
            .single()

        if (dbError) return { error: dbError.message }

        const fullData = await kanbify.from('attachments')
            .select('*, profiles:created_by ( id, full_name, avatar_url )')
            .eq('id', data.id)
            .single()

        if (fullData.data) setAttachments(p => sortAtt([...p, fullData.data]))
        return { data: fullData.data }
    }

    // Multi-file upload: sequential so positions stay in picked order.
    // Returns { uploaded, errors:[{name,error}] } — never throws.
    const uploadAttachments = async (files, userId, options = {}) => {
        const list = Array.from(files || [])
        let uploaded = 0
        const errors = []
        for (const file of list) {
            const { error } = await uploadAttachment(file, userId, options)
            if (error) errors.push({ name: file.name, error })
            else uploaded++
        }
        return { uploaded, errors }
    }

    const linkAttachmentsToComment = async (attachmentIds = [], commentId) => {
        const ids = Array.from(new Set((attachmentIds || []).filter(Boolean)))
        if (!ids.length || !commentId) return { success: true }
        const results = await Promise.all(ids.map((id) => (
            kanbify.from('attachments').update({ comment_id: commentId }).eq('id', id)
        )))
        const failed = results.find((result) => result?.error)
        if (failed) return { success: false, error: failed.error.message }
        setAttachments((prev) => sortAtt(prev.map((att) => (
            ids.includes(att.id) ? { ...att, comment_id: commentId } : att
        ))))
        return { success: true }
    }

    // Move one attachment up/down by one slot. Renumbers the whole list to
    // 0..n-1 and persists only the rows whose position actually changed.
    const moveAttachment = async (id, direction) => {
        const ordered = sortAtt(attachments)
        const idx = ordered.findIndex(a => a.id === id)
        if (idx === -1) return { success: false }
        const swap = direction === 'up' ? idx - 1 : idx + 1
        if (swap < 0 || swap >= ordered.length) return { success: false }

        const reordered = [...ordered]
        ;[reordered[idx], reordered[swap]] = [reordered[swap], reordered[idx]]

        const renumbered = reordered.map((a, i) => ({ ...a, position: i }))
        const prevSnapshot = attachments
        setAttachments(renumbered) // optimistic

        const changed = renumbered.filter((a, i) => (ordered[i]?.id !== a.id))
        const results = await Promise.all(
            changed.map(a => kanbify.from('attachments')
                .update({ position: a.position }).eq('id', a.id))
        )
        const failed = results.find(r => r?.error)
        if (failed) {
            setAttachments(prevSnapshot) // rollback
            return { success: false, error: failed.error.message }
        }
        return { success: true }
    }

    const removeAttachment = async (id, filePath) => {
        const urlParts = filePath.split('/card_attachments/')
        if (urlParts.length > 1) {
            const storagePath = urlParts[1]
            const { error: storageError } = await kanbify.storage.from('card_attachments').remove([storagePath])
            if (storageError) logDevError('Failed to remove attachment from storage:', storageError.message)
        }
        const { error: dbError } = await kanbify.from('attachments').delete().eq('id', id)
        if (dbError) {
            logDevError('Failed to remove attachment record:', dbError.message)
            return { success: false, error: dbError.message }
        }
        setAttachments(p => p.filter(a => a.id !== id))
        return { success: true, error: null }
    }

    return { attachments, fetch, uploadAttachment, uploadAttachments, linkAttachmentsToComment, moveAttachment, removeAttachment }
}

/* ========== PROCEDURE ATTACHMENTS ========== */
// Files attached to a PROCEDURE (pdf/excel/word/images). Mirrors useAttachments
// but keyed to procedure_id + its own storage bucket, so the card-attachment
// path (and its NOT NULL card_id anchor) stays untouched.
export function useProcedureAttachments(procedureId) {
    const [attachments, setAttachments] = useState([])

    const sortAtt = (arr) => [...arr].sort((a, b) =>
        (a.position ?? 0) - (b.position ?? 0) ||
        new Date(a.created_at) - new Date(b.created_at)
    )

    const fetch = useCallback(async () => {
        if (!procedureId) { setAttachments([]); return }
        const { data } = await kanbify
            .from('procedure_attachments')
            .select('*, profiles:created_by ( id, full_name, avatar_url )')
            .eq('procedure_id', procedureId)
            .order('position', { ascending: true })
            .order('created_at', { ascending: true })
        setAttachments(sortAtt(data || []))
    }, [procedureId])

    useEffect(() => { fetch() }, [fetch])

    const uploadAttachment = async (file, userId) => {
        // Video files (how-to clips on procedures) get a higher cap than docs/images.
        const isVid = (file.type || '').startsWith('video/') || /\.(mp4|webm|mov|m4v|ogg)$/i.test(file.name || '')
        const MAX_FILE_SIZE = isVid ? 100 * 1024 * 1024 : 10 * 1024 * 1024
        if (file.size > MAX_FILE_SIZE) {
            const mb = (MAX_FILE_SIZE / 1024 / 1024).toFixed(0)
            return { error: `Fișierul este prea mare. Dimensiunea maximă este ${mb}MB (fișierul tău: ${(file.size / 1024 / 1024).toFixed(1)}MB).` }
        }
        const fileExt = file.name.split('.').pop()
        const fileName = `${procedureId}/${Math.random().toString(36).substring(2)}.${fileExt}`
        const { error: uploadError } = await kanbify.storage.from('procedure-attachments').upload(fileName, file)
        if (uploadError) return { error: uploadError.message }
        const { data: { publicUrl } } = kanbify.storage.from('procedure-attachments').getPublicUrl(fileName)

        let nextPos = 0
        setAttachments(prev => { nextPos = prev.reduce((m, a) => Math.max(m, (a.position ?? 0) + 1), 0); return prev })

        const { data, error: dbError } = await kanbify.from('procedure_attachments')
            .insert([{ procedure_id: procedureId, name: file.name, url: publicUrl, type: file.type, created_by: userId, position: nextPos }])
            .select()
            .single()
        if (dbError) return { error: dbError.message }

        const fullData = await kanbify.from('procedure_attachments')
            .select('*, profiles:created_by ( id, full_name, avatar_url )')
            .eq('id', data.id)
            .single()
        if (fullData.data) setAttachments(p => sortAtt([...p, fullData.data]))
        return { data: fullData.data }
    }

    const uploadAttachments = async (files, userId) => {
        const list = Array.from(files || [])
        let uploaded = 0
        const errors = []
        for (const file of list) {
            const { error } = await uploadAttachment(file, userId)
            if (error) errors.push({ name: file.name, error })
            else uploaded++
        }
        return { uploaded, errors }
    }

    const moveAttachment = async (id, direction) => {
        const ordered = sortAtt(attachments)
        const idx = ordered.findIndex(a => a.id === id)
        if (idx === -1) return { success: false }
        const swap = direction === 'up' ? idx - 1 : idx + 1
        if (swap < 0 || swap >= ordered.length) return { success: false }
        const reordered = [...ordered]
        ;[reordered[idx], reordered[swap]] = [reordered[swap], reordered[idx]]
        const renumbered = reordered.map((a, i) => ({ ...a, position: i }))
        const prevSnapshot = attachments
        setAttachments(renumbered)
        const changed = renumbered.filter((a, i) => (ordered[i]?.id !== a.id))
        const results = await Promise.all(changed.map(a =>
            kanbify.from('procedure_attachments').update({ position: a.position }).eq('id', a.id)))
        const failed = results.find(r => r?.error)
        if (failed) { setAttachments(prevSnapshot); return { success: false, error: failed.error.message } }
        return { success: true }
    }

    const removeAttachment = async (id, filePath) => {
        const urlParts = (filePath || '').split('/procedure-attachments/')
        if (urlParts.length > 1) {
            const storagePath = urlParts[1]
            const { error: storageError } = await kanbify.storage.from('procedure-attachments').remove([storagePath])
            if (storageError) logDevError('Failed to remove procedure attachment from storage:', storageError.message)
        }
        const { error: dbError } = await kanbify.from('procedure_attachments').delete().eq('id', id)
        if (dbError) { logDevError('Failed to remove procedure attachment record:', dbError.message); return { success: false, error: dbError.message } }
        setAttachments(p => p.filter(a => a.id !== id))
        return { success: true, error: null }
    }

    return { attachments, fetch, uploadAttachment, uploadAttachments, moveAttachment, removeAttachment }
}
